import { User } from "../../../core/models/entities/User";
import { CreateUserDto } from "../../../core/models/dtos/CreateUserDto";
import { Dialog_VM, DialogFrame, useDialog } from "../Dialogs/dialog-base";
import { Section } from "../StructureElements/Section";
import { SectionHeader } from "../../screens/my-profile/SectionHeader";
import { PrimaryButton } from "../Buttons/PrimaryButton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
import { SectionContent } from "../../screens/my-profile/SectionContent";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { InputWrapper } from "../Input/InputWrapper";
import { InputLabel } from "../Input/InputLabel";
import { CustomInput } from "../Input/CustomInput";
import { CustomPhoneInput } from "../Input/CustomPhoneInput";
import { nameOf } from "../../../utils/functions/ReflectionUtil";
import { SectionHeaderTypography } from "../Typographies/SectionHeaderTypography";
import { BoxHeaderTypography } from "../Typographies/BoxHeaderTypography";
import { CustomInputChangeEvent } from "../../../utils/models/events/CustomInputChangeEvent";
import { formatPhoneNumberIntl } from "react-phone-number-input";

export function UserList({
  users,
  onCreate,
  onDelete,
  onToggle,
}: {
  users: User[];
  onCreate?: (user: CreateUserDto) => Promise<any>;
  onDelete?: (userId: number) => Promise<void>;
  onToggle?: (isOpen: boolean) => void;
}) {
  const { t } = useTranslation();
  const [userState, setUserState] = useState(users);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    setUserState(users);
  }, [users]);

  const createUserDialog = useDialog();

  function handleCreateUser(user: CreateUserDto) {
    if (onCreate) onCreate(user).then(createUserDialog.closeDialog);
  }

  async function handleDeleteUser(user: User) {
    if (!onDelete) return;
    if (confirm(t("compMisc.deleteUserConfirm", { email: user.email }))) {
      await onDelete(user.id);
      setUserState(userState.filter((u) => u.id != user.id));
    }
  }

  return (
    <Section>
      <CreateUserDialog
        dialogOptions={createUserDialog}
        onCreate={handleCreateUser}
      />
      <SectionHeader
        isCloseable={true}
        initialState={isOpen}
        onToggle={(open) => {
          setIsOpen(open);
          onToggle?.(open);
        }}
      >
        <SectionHeaderTypography>{t("compMisc.users")}</SectionHeaderTypography>
      </SectionHeader>
      <SectionContent close={!isOpen}>
        <div className={"flex flex-col w-full"}>
        <div className={"border rounded flex-1 overflow-y-auto max-h-96"}>
          <table className={"table-auto w-full"}>
            <thead className={"text-left sticky top-0 bg-[#FCFDFE]"}>
              <tr>
                <th className={"py-3 pl-4 font-medium text-gray-600 text-xs"}>
                  {t("first_name")}
                </th>
                <th className={"py-3 pr-4 font-medium text-gray-600 text-xs"}>
                  {t("last_name")}
                </th>
                <th className={"py-3 px-4 font-medium text-gray-600 text-xs"}>
                  {t("email")}
                </th>
                <th className={"py-3 px-4 font-medium text-gray-600 text-xs"}>
                  {t("compMisc.phone")}
                </th>
                <th
                  className={"py-3 px-4 font-medium text-gray-600 text-xs w-20"}
                >
                  {t("compMisc.actions")}
                </th>
              </tr>
            </thead>
            <tbody>
              {userState.map((x) => (
                <tr key={x.id} className={"border-b hover:bg-gray-50"}>
                  <td className={"py-2 pl-4 text-xs"}>{x.firstName}</td>
                  <td className={"py-2 pr-4 text-xs"}>{x.lastName}</td>
                  <td className={"py-2 px-4 text-xs"}>{x.email}</td>
                  <td className={"py-2 px-4 text-xs"}>
                    {x.phone ? formatPhoneNumberIntl(x.phone) : ""}
                  </td>
                  <td className={"py-2 px-4 text-xs"}>
                    <PrimaryButton
                      width={"sm"}
                      className={"px-4"}
                      onClick={() => handleDeleteUser(x)}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                      <span className="flex-1">{t("delete")}</span>
                    </PrimaryButton>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className={"mt-4"}>
          <PrimaryButton
            width={"md"}
            className={"px-4"}
            onClick={createUserDialog.openDialog}
          >
            <FontAwesomeIcon icon={faPlus} />
            <span className="flex-1">{t("compMisc.createNewUser")}</span>
          </PrimaryButton>
        </div>
        </div>
      </SectionContent>
    </Section>
  );
}

interface CreateUserDialogProps {
  dialogOptions: Dialog_VM;
  onCreate: (user: CreateUserDto) => void;
  /**
   * Customer onboarding (flag customerOnboardingEnabled, API flag
   * WelcomeMailCcEnabled): offer "Also send a copy to the primary contact
   * ({email})" under Phone and hand the choice on as
   * CreateUserDto.ccPrimaryContact. Only the customer-update section passes
   * it, and only with the flag on and a primary contact e-mail present; the
   * checkbox is disabled (and off) while the typed e-mail IS the contact's.
   * Omitted (every other caller) = the exact current dialog and dto.
   */
  ccPrimaryContact?: { email: string };
}

type RequiredField = "firstname" | "lastname" | "email" | "phone";

export function CreateUserDialog({
  dialogOptions,
  onCreate,
  ccPrimaryContact,
}: CreateUserDialogProps) {
  const { t } = useTranslation();
  const [createUser, setCreateUser] = useState<CreateUserDto>({
    email: "",
    firstname: "",
    lastname: "",
    phone: "",
    organizationId: 0,
  });
  const [errors, setErrors] = useState<Partial<Record<RequiredField, boolean>>>({});
  const [ccChecked, setCcChecked] = useState(false);
  // The copy is an extra mail to the contact: never inherited silently by
  // the next "Add user" - forget the choice when the dialog closes. (A no-op
  // render-wise for every caller without the checkbox: false -> false.)
  useEffect(() => {
    if (!dialogOptions.isOpen) setCcChecked(false);
  }, [dialogOptions.isOpen]);
  const ccEmail = ccPrimaryContact?.email.trim() ?? "";
  const ccIsRecipient =
    !!ccEmail &&
    ccEmail.toLowerCase() === createUser.email.trim().toLowerCase();

  function handleInputChange(
    event: React.ChangeEvent<HTMLInputElement> | CustomInputChangeEvent,
  ) {
    const target = event.target;
    const value = target.type === "checkbox" ? target.checked : target.value;
    const name = target.name;

    setCreateUser((x) => ({
      ...x,
      [name]: value,
    }));

    if (errors[name as RequiredField]) {
      setErrors((prev) => ({ ...prev, [name]: false }));
    }
  }

  function handleSubmit() {
    const newErrors: Partial<Record<RequiredField, boolean>> = {};
    if (!createUser.firstname?.trim()) newErrors.firstname = true;
    if (!createUser.lastname?.trim()) newErrors.lastname = true;
    if (!createUser.email?.trim()) newErrors.email = true;
    if (!createUser.phone?.trim()) newErrors.phone = true;

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // The cc choice travels only when the checkbox was offered; a copy to
    // the recipient's own address is never requested.
    onCreate(
      ccPrimaryContact
        ? { ...createUser, ccPrimaryContact: ccChecked && !ccIsRecipient }
        : createUser,
    );
  }

  const errorRing = "ring-2 ring-red-500 border-red-500";
  const requiredMark = <span className="text-red-500 ml-0.5">*</span>;

  return (
    <DialogFrame dialogOption={dialogOptions}>
      <div className={"space-y-6"}>
        <BoxHeaderTypography>
          {t("compMisc.createNewUserTitle")}
        </BoxHeaderTypography>
        <div className={"space-y-4"}>
          <InputWrapper>
            <InputLabel>{t("first_name")}{requiredMark}</InputLabel>
            <CustomInput
              name={nameOf<CreateUserDto>((e) => e.firstname)}
              value={createUser.firstname}
              onChange={(e) => handleInputChange(e)}
              className={errors.firstname ? errorRing : ""}
            />
          </InputWrapper>
          <InputWrapper>
            <InputLabel>{t("last_name")}{requiredMark}</InputLabel>
            <CustomInput
              name={nameOf<CreateUserDto>((e) => e.lastname)}
              value={createUser.lastname}
              onChange={(e) => handleInputChange(e)}
              className={errors.lastname ? errorRing : ""}
            />
          </InputWrapper>
          <InputWrapper>
            <InputLabel>{t("email")}{requiredMark}</InputLabel>
            <CustomInput
              name={nameOf<CreateUserDto>((e) => e.email)}
              value={createUser.email}
              onChange={(e) => handleInputChange(e)}
              className={errors.email ? errorRing : ""}
            />
          </InputWrapper>
          <InputWrapper>
            <InputLabel>{t("compMisc.phone")}{requiredMark}</InputLabel>
            <CustomPhoneInput
              name={nameOf<CreateUserDto>((e) => e.phone)}
              value={createUser.phone}
              onChange={(e) => handleInputChange(e)}
              disabled={false}
              className={errors.phone ? errorRing : ""}
            />
          </InputWrapper>
          {ccPrimaryContact && ccEmail && (
            <label className="flex cursor-pointer items-center gap-2 text-xs text-gray-700">
              <input
                type="checkbox"
                name="ccPrimaryContact"
                className="h-4 w-4"
                checked={ccChecked && !ccIsRecipient}
                disabled={ccIsRecipient}
                onChange={(e) => setCcChecked(e.target.checked)}
              />
              <span>
                {t("customerUpdate.ccPrimaryContact", { email: ccEmail })}
              </span>
            </label>
          )}
          <div className={"flex justify-center"}>
            <PrimaryButton width={"md"} onClick={handleSubmit}>
              {t("compMisc.create")}
            </PrimaryButton>
          </div>
        </div>
      </div>
    </DialogFrame>
  );
}
